import debounce from "lodash.debounce";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { styled } from "styled-components";

import { InfinityLoader, SectionTitleUnderlined, Spinner, StyledGrid } from "common/components";
import { UIInput } from "common/components/form/UIInput";
import { breakpoints, mediaStyles } from "common/styles";
import { useDispatch, useMemoisedSelector, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import type { ScrollPositions } from "context/session/sessionTypes";
import { useSession } from "context/session/useSession";
import { fetchFilteredTopics } from "domains/filteredTopicList/actions";
import {
  getFilteredTopicsAreFetching,
  getFilteredTopicsIds,
  getFilteredTopicsIsPossibleNextPage,
} from "domains/filteredTopicList/selectors";
import { getSiteLanguage } from "domains/language/selectors";
import { TopicTile } from "domains/topic/components/topicTile/TopicTile";
import { createTopicsSelector } from "entities/topics/selectors";

const StyledInnerContainer = styled.div`
  ${mediaStyles(({ spacing }) => `margin: 0 ${spacing}rem ${spacing}rem ${spacing}rem;`)};
`;

const StyledUIInput = styled(UIInput)`
  @media ${breakpoints.medium} {
    width: fit-content;
  }
`;

const StyledSpinner = styled(Spinner)`
  margin: 3rem auto;
  display: block;
  color: ${({ theme }) => theme.colors.textBright};
`;

const StyledNoResultsText = styled.div`
  color: ${({ theme }) => theme.colors.textBright};
`;

export const SearchableTopicList = () => {
  const dispatch = useDispatch();
  const topics = useMemoisedSelector(getFilteredTopicsIds, createTopicsSelector);
  const siteLanguage = useSelector(getSiteLanguage);
  const isFetching = useSelector(getFilteredTopicsAreFetching);
  const isPossibleNextPage = useSelector(getFilteredTopicsIsPossibleNextPage);
  const { scrollPositions, updateSession } = useSession();
  const [filterTerm, setFilterTerm] = useState<string>("");
  const translate = useTranslate();

  const scrollPositionsRef = useRef<ScrollPositions | undefined>(scrollPositions);

  useLayoutEffect(() => {
    if (scrollPositionsRef.current?.discoverPage === undefined) return;
    scrollTo(0, scrollPositionsRef.current.discoverPage);
    updateSession((state) => ({ ...state, scrollPositions: { ...state.scrollPositions, discoverPage: undefined } }));
  }, [updateSession]);

  useEffect(() => {
    fetchTopics(filterTerm, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteLanguage]);

  const fetchTopics = (term: string, isInvalidating = false) => {
    const offset = isInvalidating ? 0 : topics.length;
    if (!isFetching) {
      dispatch(
        fetchFilteredTopics({
          limit: 6 * 8,
          offset,
          filterTerm: term,
        }),
      );
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedTopicsSearch = useCallback(
    debounce((term: string) => {
      fetchTopics(term, true);
    }, 500),
    [],
  );

  const onInput = (value: string) => {
    setFilterTerm(value);
    debouncedTopicsSearch(value);
  };

  const onClear = () => {
    setFilterTerm("");
    debouncedTopicsSearch("");
  };

  return (
    <>
      <StyledInnerContainer data-test-id="searchable-topic-list-topics">
        <SectionTitleUnderlined
          dataTestId="all-topics-section-title"
          rightElement={
            <StyledUIInput
              clear={onClear}
              onChange={onInput}
              placeholder={translate("app.search.placeholder")}
              value={filterTerm}
            />
          }
        >
          {translate("app.topic.all_topics")}
        </SectionTitleUnderlined>
        {isFetching && topics.length === 0 && <StyledSpinner />}
        {topics.length !== 0 && (
          <InfinityLoader
            isPending={isFetching}
            loadNextPage={() => fetchTopics(filterTerm, false)}
            hasNextPage={isPossibleNextPage}
          >
            <StyledGrid>
              {topics.map((topic) => (
                <TopicTile
                  key={topic.id}
                  topicId={topic.id}
                  topicName={topic.name}
                  provider={topic.provider}
                  topicBack={{ term: "app.discover.title" }}
                  attachment={topic.attachment ?? undefined}
                  canEdit={topic.can_edit ?? false}
                  isCustom={topic.is_custom ?? false}
                  isNew={topic.is_new ?? false}
                  isFollowing={topic.is_following ?? false}
                  imageUrl={topic.images?.x_small}
                  onClick={() =>
                    updateSession((state) => ({
                      ...state,
                      scrollPositions: { ...state.scrollPositions, discoverPage: scrollY },
                    }))
                  }
                />
              ))}
            </StyledGrid>
          </InfinityLoader>
        )}
        {topics.length === 0 && !isFetching && (
          <StyledNoResultsText>{translate("app.search.results.no_results")}</StyledNoResultsText>
        )}
      </StyledInnerContainer>
    </>
  );
};
